import * as React from 'react';
import {
  ColumnDef,
  flexRender,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  RowSelectionState,
  getPaginationRowModel,
} from '@tanstack/react-table';
import { useDebounceValue } from 'usehooks-ts';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
} from '@/components/ui/dropdown-menu';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PlannerPublish } from '../planner-publish-cte/planner-publish';
import { SendApproval } from '../approval/send-approval';
import { ArrowDown, SquarePlus, InfoIcon } from 'lucide-react';
import { useEnv } from '@/components/providers/env-provider';
import { hasPermission } from '@/utils/validation';
import {
  ADD_CTA_PERMISSIONS,
  PUBLISH_PERMISSIONS,
} from '@/config/feature-permissions';
import { useUser } from '@/components/providers/user-provider';
import { useTheme } from '@/components/providers/theme-provider';
import { Link } from 'react-router-dom';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  onNextPage: () => void;
  onPreviousPage: () => void;
  nextPageDisabled: boolean;
  previousPageDisabled: boolean;
  onSearch: (value: string) => void;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  onNextPage,
  onPreviousPage,
  nextPageDisabled,
  previousPageDisabled,
  onSearch,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});

  const [search, setSearch] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = useDebounceValue('', 100);

  React.useEffect(() => {
    onSearch(debouncedSearch);
  }, [debouncedSearch]);

  React.useEffect(() => {
    setDebouncedSearch(search);
  }, [search]);

  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  React.useEffect(() => {
    function handleResize() {
      setWindowWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const mobileBreakpoint = 800;
  const dynamicColumns = React.useMemo<ColumnDef<TData, TValue>[]>(() => {
    if (windowWidth < mobileBreakpoint) {
      // @ts-expect-error Missing hiddenOnMobile
      return columns.filter((column) => !column.meta?.hiddenOnMobile);
    }
    return columns;
  }, [windowWidth, columns]);

  const table = useReactTable({
    data,
    columns: dynamicColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const { env } = useEnv();
  const { role } = useUser();
  const { theme } = useTheme();

  return (
    <div className='w-full'>
      <div className='xl:grid grid-cols-[80%_20%]'>
        <div className='xl:mr-8'>
          <div
            className={`flex items-center justify-between py-4 flex-wrap gap-4 border-solid rounded-md border-1 px-4 ${theme === 'dark' ? 'bg-slate-800 border-slate-800' : 'bg-white border-gray-200'}`}
          >
            <Input
              placeholder='Type to search by identifier, e.g pax'
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className={`max-w-sm ${theme === 'dark' ? 'text-white' : 'text-black'}`}
            />
            <div className='flex gap-4 flex-wrap'>
              {env === 'cte' &&
                hasPermission(ADD_CTA_PERMISSIONS[env], role) && (
                  <Link
                    to='/planner/create'
                    className='flex items-center text-sm font-medium gap-2 h-8 border-solid border-2 rounded p-4 !border-slate-800 !bg-slate-800 !text-white dark:bg-white dark:border-white dark:text-black cursor-default'
                  >
                    <SquarePlus className='h-4 w-4' />
                    Create planner
                  </Link>
                )}
              <DropdownMenu>
                <DropdownMenuContent align='end'>
                  {table
                    .getAllColumns()
                    .filter((column) => column.getCanHide())
                    .map((column) => {
                      return (
                        <DropdownMenuCheckboxItem
                          key={column.id}
                          className='capitalize'
                          checked={column.getIsVisible()}
                          onCheckedChange={(value) =>
                            column.toggleVisibility(!!value)
                          }
                        >
                          {column.id}
                        </DropdownMenuCheckboxItem>
                      );
                    })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <div className='mt-8'>
            <div className='rounded-md border'>
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        return (
                          <TableHead key={header.id}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map((row) => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && 'selected'}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell key={cell.id}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className='h-24 text-center'
                      >
                        No results.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <div className='flex items-center justify-end space-x-2 py-4'>
              <div className='space-x-2'>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={onPreviousPage}
                  disabled={previousPageDisabled}
                >
                  Previous
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={onNextPage}
                  disabled={nextPageDisabled}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
        {hasPermission(PUBLISH_PERMISSIONS[env], role) && (
          <div className='flex flex-col light:bg-white py-4 px-8 border-solid border-1 rounded-md'>
            {env === 'cte' ? (
              <>
                <h4 className='text-lg font-semibold mb-4'>Publish steps</h4>
                <div className='flex flex-col mb-12'>
                  <span className='font-bold mb-2'>1. CTE</span>
                  <PlannerPublish />
                </div>
                <ArrowDown size={40} className='mx-auto mb-12' />
                <div className='flex flex-col mb-8'>
                  <p className='font-bold mb-2'> 2. Production</p>
                  <div className='flex gap-2 mb-4'>
                    <InfoIcon className='flex-shrink-0 mt-2' size={20} />
                    <p className='text-sm'>
                      Make sure your changes are published to CTE before sending
                      production approval.
                    </p>
                  </div>
                  <SendApproval />
                </div>
              </>
            ) : (
              <>
                <PlannerPublish />
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
